/**
 * TypeScript interfaces and types for the TSC Customer Portal consent interface
 * 
 * This module defines all types used in the consent collection process including:
 * - Token validation and customer identity
 * - Consent request data and form schemas
 * - Digital signature data structures
 * - API responses and error handling
 */

import { z } from "zod";
import { TranslationValues } from "next-intl";

// ===== CORE CONSENT TYPES =====

/**
 * Customer consent token data received from email link
 */
export interface ConsentToken {
  /** Unique token identifier from customer email */
  token: string;
  /** Token expiration timestamp */
  expiresAt: number;
  /** Customer identification data */
  customerId: string;
  /** Project identification */
  projectId: string;
  /** Required verification method */
  verificationType: 'zipcode' | 'email' | 'phone';
}

/**
 * Customer identity verification data
 */
export interface CustomerIdentity {
  /** Customer's postal code for verification */
  zipcode: string;
  /** Customer's city (for display/confirmation) */
  city?: string;
  /** Customer's street address (for display/confirmation) */
  address?: string;
  /** Verification status */
  verified: boolean;
}

/**
 * Project-specific consent requirements
 */
export interface ConsentRequirement {
  /** Unique consent requirement ID */
  id: string;
  /** Consent type (data processing, construction access, etc.) */
  type: 'data_processing' | 'construction_access' | 'site_inspection' | 'custom';
  /** Display title (localized) */
  title: string;
  /** Detailed description (localized) */
  description: string;
  /** Whether this consent is required or optional */
  required: boolean;
  /** Additional legal text or references */
  legalReference?: string;
}

/**
 * Assembly address information (returned after zipcode verification)
 */
export interface AssemblyAddress {
  /** Street name and number */
  street: string;
  /** Postal code */
  zipcode: string;
  /** City name */
  city: string;
  /** Country */
  country?: string;
}

/**
 * Complete consent request data for a customer/project
 */
export interface ConsentRequestData {
  /** Token information */
  token: ConsentToken;
  /** Customer identity information */
  customer: CustomerIdentity;
  /** Project information */
  project: {
    id: string;
    name: string;
    address: string;
    description?: string;
  };
  /** List of consent requirements for this project */
  requirements: ConsentRequirement[];
  /** UI configuration for the consent form */
  ui: {
    logo?: string;
    primaryColor?: string;
    companyName: string;
    contactEmail: string;
    contactPhone?: string;
  };
  /** Assembly address (available after verification) */
  assemblyAddress?: AssemblyAddress;
  /** Legacy form schema (for backward compatibility) */
  formSchema?: any;
  /** Consent type (fallback if projectDescription is null) */
  consentType?: string;
}

// ===== FORM DATA TYPES =====

/**
 * Customer-provided consent responses
 */
export interface ConsentResponse {
  /** Requirement ID this response is for */
  requirementId: string;
  /** Customer's consent decision */
  consented: boolean;
  /** Optional customer comments */
  comments?: string;
  /** Timestamp of consent decision */
  timestamp: number;
}

/**
 * Device information for signature capture
 */
export interface DeviceInfo {
  /** User agent string */
  userAgent: string;
  /** Screen resolution */
  screenResolution: string;
  /** Device type detection */
  type: 'desktop' | 'tablet' | 'mobile';
  /** Touch support capability */
  touchSupport: boolean;
  /** Platform information */
  platform: string;
  /** Viewport dimensions */
  viewport: {
    width: number;
    height: number;
  };
}

/**
 * Digital signature data
 */
export interface DigitalSignature {
  /** Base64-encoded signature image */
  signatureData: string;
  /** Signature capture timestamp */
  timestamp: number;
  /** Device information for audit trail */
  deviceInfo: DeviceInfo;
  /** Signature canvas dimensions for validation */
  canvasDimensions: {
    width: number;
    height: number;
  };
}

/**
 * Complete consent submission data
 */
export interface ConsentSubmission {
  /** Token used for this submission */
  token: string;
  /** Customer identity verification */
  customerVerification: CustomerIdentity;
  /** All consent responses */
  responses: ConsentResponse[];
  /** Digital signature */
  signature: DigitalSignature;
  /** Submission metadata */
  metadata: {
    submittedAt: number;
    ipAddress?: string;
    browserInfo: string;
    sessionDuration: number;
  };
}

export type ConsentFinalResponseType = 'accepted' | 'rejected';

export interface ConsentAcceptanceSubmission {
  token: string;
  responseType: ConsentFinalResponseType;
  formData: Record<string, unknown>;
  zipcode?: string;
  digitalSignature?: {
    signatureData: string;
    signatureMetadata: {
      width: number;
      height: number;
      timestamp: string;
      device: string;
    };
  };
  metadata?: {
    screenResolution?: string;
    timezone?: string;
  };
}

// ===== API RESPONSE TYPES =====

/**
 * Standard API response wrapper
 */
export interface ConsentAPIResponse<T = any> {
  /** Whether the request was successful */
  success: boolean;
  /** Response data (if successful) */
  data?: T;
  /** Error message (if failed) */
  error?: string;
  /** Additional error details */
  details?: string[];
  /** Response timestamp */
  timestamp: number;
}

/**
 * Token validation response
 */
export interface TokenValidationResponse extends ConsentAPIResponse<ConsentRequestData> {
  /** Whether the token is valid and not expired */
  valid: boolean;
  /** Time remaining before token expires (in seconds) */
  timeRemaining?: number;
  /** Error code for specific error types */
  errorCode?: 'EXPIRED' | 'INVALID' | 'ALREADY_COMPLETED' | 'NOT_FOUND';
}

/**
 * Zipcode verification response
 */
export interface ZipcodeVerificationResponse extends ConsentAPIResponse {
  /** Whether the zipcode matches customer records */
  verified: boolean;
  /** Matched address information (for confirmation) */
  addressInfo?: {
    street: string;
    city: string;
    zipcode: string;
  };
  /** Full assembly address (returned after successful verification) */
  assemblyAddress?: AssemblyAddress;
  /** Formatted address string for display */
  formattedAddress?: string;
}

/**
 * Consent submission response
 */
export interface ConsentSubmissionResponse extends ConsentAPIResponse {
  /** Unique submission reference ID */
  submissionId: string;
  /** Confirmation code for customer records */
  confirmationCode: string;
  /** Legal document reference */
  legalReference: string;
  completedAt?: string;
  status?: 'completed' | 'declined';
  callbackTriggered?: boolean;
  responseType?: ConsentFinalResponseType;
}

/**
 * Consent status tracking
 */
export interface ConsentStatus {
  /** Current status of consent process */
  status: 'pending' | 'in_progress' | 'completed' | 'expired' | 'cancelled';
  /** Steps completed so far */
  stepsCompleted: Array<'token_validated' | 'identity_verified' | 'form_completed' | 'signature_captured' | 'submitted'>;
  /** Current step in progress */
  currentStep: string;
  /** Last activity timestamp */
  lastActivity: number;
  /** Submission reference (if completed) */
  submissionReference?: string;
}

// ===== VALIDATION SCHEMAS =====

/**
 * Error message keys for localization
 */
export type ConsentValidationMessages = 
  | "token-required"
  | "token-invalid"
  | "zipcode-required"
  | "zipcode-invalid"
  | "signature-required" 
  | "consent-required"
  | "invalid-type"
  | "network-error";

/**
 * Default validation messages (fallback)
 */
export const defaultConsentMessages: Record<ConsentValidationMessages, string> = {
  "token-required": "Access token is required",
  "token-invalid": "Invalid or expired access token",
  "zipcode-required": "Postal code is required for verification",
  "zipcode-invalid": "Invalid postal code format",
  "signature-required": "Digital signature is required",
  "consent-required": "Consent decision is required",
  "invalid-type": "Invalid input format",
  "network-error": "Network connection error"
};

/**
 * Zipcode validation schema
 */
export const zipcodeValidationSchema = (
  t?: (key: ConsentValidationMessages, values?: TranslationValues) => string
) => z.object({
  zipcode: z
    .string({
      required_error: t?.("zipcode-required") ?? defaultConsentMessages["zipcode-required"],
      invalid_type_error: t?.("invalid-type") ?? defaultConsentMessages["invalid-type"]
    })
    .regex(/^\d{5}$/, {
      message: t?.("zipcode-invalid") ?? defaultConsentMessages["zipcode-invalid"]
    })
});

/**
 * Consent response validation schema
 */
export const consentResponseSchema = (
  t?: (key: ConsentValidationMessages, values?: TranslationValues) => string
) => z.object({
  requirementId: z.string({
    required_error: t?.("consent-required") ?? defaultConsentMessages["consent-required"]
  }),
  consented: z.boolean({
    required_error: t?.("consent-required") ?? defaultConsentMessages["consent-required"]
  }),
  comments: z.string().optional(),
  timestamp: z.number()
});

/**
 * Digital signature validation schema
 */
export const signatureValidationSchema = (
  t?: (key: ConsentValidationMessages, values?: TranslationValues) => string
) => z.object({
  signatureData: z
    .string({
      required_error: t?.("signature-required") ?? defaultConsentMessages["signature-required"]
    })
    .min(1, {
      message: t?.("signature-required") ?? defaultConsentMessages["signature-required"]
    }),
  timestamp: z.number(),
  deviceInfo: z.object({
    userAgent: z.string(),
    screenResolution: z.string(),
    deviceType: z.enum(['desktop', 'tablet', 'mobile']),
    touchSupport: z.boolean()
  }),
  canvasDimensions: z.object({
    width: z.number().positive(),
    height: z.number().positive()
  })
});

/**
 * Complete submission validation schema
 */
export const consentSubmissionSchema = (
  t?: (key: ConsentValidationMessages, values?: TranslationValues) => string
) => z.object({
  token: z.string({
    required_error: t?.("token-required") ?? defaultConsentMessages["token-required"]
  }),
  customerVerification: zipcodeValidationSchema(t).extend({
    verified: z.boolean()
  }),
  responses: z.array(consentResponseSchema(t)).min(1),
  signature: signatureValidationSchema(t),
  metadata: z.object({
    submittedAt: z.number(),
    ipAddress: z.string().optional(),
    browserInfo: z.string(),
    sessionDuration: z.number()
  })
});

// ===== EXPORTED TYPE INFERENCE =====

export type ZipcodeValidation = z.infer<ReturnType<typeof zipcodeValidationSchema>>;
export type ConsentResponseValidation = z.infer<ReturnType<typeof consentResponseSchema>>;
export type SignatureValidation = z.infer<ReturnType<typeof signatureValidationSchema>>;
export type ConsentSubmissionValidation = z.infer<ReturnType<typeof consentSubmissionSchema>>;

// ===== UI STATE TYPES =====

/**
 * Consent flow step enumeration
 */
export enum ConsentStep {
  TOKEN_VALIDATION = 'token_validation',
  IDENTITY_VERIFICATION = 'identity_verification', 
  CONSENT_FORM = 'consent_form',
  DIGITAL_SIGNATURE = 'signature_capture',
  CONFIRMATION = 'confirmation',
  SUCCESS = 'success',
  ERROR = 'error'
}

/**
 * UI state for consent flow management
 */
export interface ConsentFlowState {
  /** Current step in the flow */
  currentStep: ConsentStep;
  /** Whether step is in loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Validated token data */
  tokenData: ConsentRequestData | null;
  /** Customer verification status */
  customerVerified: boolean;
  /** Form responses collected so far */
  responses: ConsentResponse[];
  /** Captured signature data */
  signature: DigitalSignature | null;
  /** Submission result */
  submissionResult: ConsentSubmissionResponse | null;
  /** Session start time */
  sessionStartTime: number;
}

/**
 * Progress indicator data
 */
export interface ProgressStep {
  /** Step identifier */
  key: ConsentStep;
  /** Display label (localized key) */
  labelKey: string;
  /** Whether step is completed */
  completed: boolean;
  /** Whether step is currently active */
  active: boolean;
  /** Whether step is accessible */
  enabled: boolean;
}